import { Select, Spin } from "antd";
import TextArea from "antd/lib/input/TextArea";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { iif } from "src/common/utils/functions";
import { UserTableColumnGenerator } from "src/components/InfoTab/UsersTable/userTableColumnGenerator";
import { isEmptyString, isNullOrUndefined } from "../../../../../common/utils";
import { AuthZTable } from "../../../../../components/authZTable";
import { useGetIndividualInclusions } from "../../common/hooks/GetIndividualInclusions";
import { KeyConditionAction, KeyConditionActions } from "../../model";
import { SpecialConditionReasonPlaceholder } from "../../SelectKeyCondition";
import { SearchByUsersController } from "./controller";
import "./index.css";
import { IndividualInclusionMemberToolTip } from "src/components/individualInclusionMemberToolTip";
import { useIsIndividualInclusionNamingEnabled } from "../../common/hooks/IsIndividualInclusionNamingEnabled";

export const NoUsersFoundMessage = "No users";
export const SearchByUsersPlaceholder = "Search by name or employee id";
export const InitialDropdownMessage = "Please enter some characters for suggestions";
export const DropdownMessageFullName = "Please enter at least 3 characters in the name";
export const DropdownMessageEmpId = "Please enter 5 digit employee id";

function SelectedUserRepresentation({ selectedUser, isIndividualInclusionMember }) {
  return (
    <>
      <div className="selected-user-name" style={{ fontSize: "24px" }}>
        {selectedUser.fullName}
        {isIndividualInclusionMember ? (
          <IndividualInclusionMemberToolTip title={"Individual Inclusion"} />
        ) : null}
      </div>
      <div className="selected-user-employee-id" style={{ fontSize: "18px" }}>
        {selectedUser.employeeId}
      </div>
    </>
  );
}

function IndividualInclusionReasonComponent({ individualInclusionsList, selectedUser }) {
  const dispatch = useDispatch();
  const specialCondition = useSelector((rootState) => rootState.keyCondition.specialConditionNode);
  const action = useSelector((rootState) => rootState.keyCondition.action);
  const [specialConditionReason, setSpecialConditionReason] = useState(specialCondition?.reason);
  const isSpecialConditionChecked = useSelector(
    (rootState) => rootState.keyCondition.isSpecialConditionChecked
  );
  function isIndividualInclusionMember() {
    if (isNullOrUndefined(individualInclusionsList)) {
      return false;
    }
    return individualInclusionsList?.includes(selectedUser?.key);
  }

  return (
    <div style={{ width: "100%" }}>
      <div className="label">
        {" "}
        Reason <span className="individual-inclusion-member-text">*</span>
      </div>
      <TextArea
        className={specialConditionReason?.length > 0 ? "" : "mandatory-special-reason"}
        value={specialCondition?.reason}
        disabled={
          isSpecialConditionChecked === false ||
          (isIndividualInclusionMember() && action !== KeyConditionAction.EDIT)
        }
        style={{ width: "400px", minHeight: "40px" }}
        placeholder={SpecialConditionReasonPlaceholder}
        autoSize={{ maxRows: 3 }}
        onChange={(event) => {
          dispatch(KeyConditionActions.setSpecialConditionReason(event.target.value));
          setSpecialConditionReason(event.target.value);
        }}
      />
    </div>
  );
}

function Add({ setCurrentStepState, setDisplayLabels, userGroupUniqueName }) {
  const [placeholderText, setPlaceholderText] = useState(SearchByUsersPlaceholder);
  const {
    searchUsersWithName,
    renderDropdownWithPagination,
    userInput,
    options,
    progress,
    onUserSelect,
    selectedUser,
    searchValue,
  } = SearchByUsersController(setCurrentStepState, setDisplayLabels);

  const excludedMembers = useSelector((rootState) =>
    rootState.membersMatchingConditionList.excludedMembers.map(
      (excludedMember) => excludedMember.userId
    )
  );
  const isIndividualInclusionNamingEnabled = useIsIndividualInclusionNamingEnabled();

  const individualInclusionsList = useGetIndividualInclusions(userGroupUniqueName);

  function setInputValue() {
    if (isNullOrUndefined(userInput) || isEmptyString(userInput.value)) {
      setPlaceholderText(SearchByUsersPlaceholder);
    } else {
      setPlaceholderText("");
    }
  }

  function notFoundContent() {
    if (progress !== null) return <Spin size="small" />;

    if (isNullOrUndefined(userInput) || isEmptyString(userInput.value))
      return InitialDropdownMessage;

    if (userInput.value.length >= userInput.minChars) return NoUsersFoundMessage;

    if (userInput.isNumber) return DropdownMessageEmpId;
    else return DropdownMessageFullName;
  }

  function onSearch(value) {
    const isNumber = /^\d+$/.test(value);
    if (isNumber)
      searchUsersWithName({
        isNumber: true,
        minChars: 5,
        value: value,
      });
    else
      searchUsersWithName({
        isNumber: false,
        minChars: 3,
        value: value,
      });
  }

  const isError =
    userInput && userInput.value.length > 0 ? userInput.value.length < userInput.minChars : false;

  const columns = UserTableColumnGenerator(
    [
      {
        title: "User",
        dataIndex: "user",
        key: "user",
      },
      {
        title: "Job Profile",
        dataIndex: "jobProfile",
        key: "jobProfile",
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
      },
      {
        title: "Department",
        dataIndex: "department",
        key: "department",
        render: "TextWithSubTitleComponent",
        className: "departmentAppInfo",
      },
      {
        title: "Home Office",
        dataIndex: "homeOffice",
        key: "homeOffice",
        render: "TextWithSubTitleComponent",
        className: "homeOfficeAppInfo",
      },
      {
        title: "Working Office",
        dataIndex: "workingOffice",
        key: "workingOffice",
        render: "TextWithSubTitleComponent",
        className: "workingOfficeAppInfo",
      },
      {
        title: "Project",
        dataIndex: "projects",
        key: "projects",
        className: "projectAppInfo",
      },
      {
        title: "Primary Organization Home",
        dataIndex: "primaryOrganizationalHome",
        key: "primaryOrganizationalHome",
      },
    ],
    individualInclusionsList,
    false,
    false,
    excludedMembers
  );

  const seletedUserDataInTableFormat = iif(function convertSelectedUserToTableUserFormat() {
    return isNullOrUndefined(selectedUser)
      ? []
      : [
          {
            key: selectedUser.key,
            user: { first: selectedUser.fullName, second: selectedUser.employeeId },
            jobProfile: selectedUser.jobProfile,
            email: selectedUser.email,
            department: { first: selectedUser.department, second: selectedUser.departmentCode },
            homeOffice: { first: selectedUser.homeOffice, second: selectedUser.homeCountry },
            workingOffice: {
              first: selectedUser.workingOffice,
              second: selectedUser.workingOfficeCountry,
            },
            projects: selectedUser.projects,
            primaryOrganizationalHome: selectedUser.primaryOrganizationalHome,
          },
        ];
  });

  return (
    <div className="search-by-individual-member">
      <div className="input-fields">
        <div>
          <div className="label"> Search member</div>
          <Select
            showSearch={isNullOrUndefined(selectedUser)}
            value={searchValue}
            placeholder={placeholderText}
            searchValue={userInput?.value}
            options={options}
            notFoundContent={notFoundContent()}
            onSearch={onSearch}
            dropdownRender={renderDropdownWithPagination}
            onBlur={setInputValue}
            onChange={onUserSelect}
            optionFilterProp={"value"}
            status={isError ? "error" : ""}
            allowClear={true}
          />
        </div>
        <IndividualInclusionReasonComponent
          individualInclusionsList={individualInclusionsList}
          selectedUser={selectedUser}
        />
      </div>
      {!isNullOrUndefined(selectedUser) ? (
        <>
          <div className="label">
            Note** - The selected user will be added as {" "}
            {isIndividualInclusionNamingEnabled ? "an individual inclusion" : "a special member"} to the
            team, and therefore, their membership should be reviewed regularly.
          </div>
          <div className="label">Selected user details</div>
          <AuthZTable
            name={"individual-inclusion-table"}
            id={"individual-inclusion-table"}
            columns={columns}
            dataSource={seletedUserDataInTableFormat}
            className="individual-inclusion-table"
            pagination={false}
          />
        </>
      ) : null}
    </div>
  );
}

function Edit({ setCurrentStepState, setDisplayLabels, userGroupUniqueName }) {
  const selectedUser = useSelector((rootState) => rootState.keyCondition.selectedUser);
  const individualInclusionsList = useGetIndividualInclusions(userGroupUniqueName);

  return (
    <div className="edit-individual-inclusion-container">
      <SelectedUserRepresentation selectedUser={selectedUser} isIndividualInclusionMember={true} />
      <div className="key-condition-inputs-heading">Edit special condition fields</div>
      <div className="key-condition-line-separation"></div>
      <IndividualInclusionReasonComponent
        individualInclusionsList={individualInclusionsList}
        selectedUser={selectedUser}
      />
    </div>
  );
}

function ByIndividualInclusion({ setCurrentStepState, setDisplayLabels, userGroupUniqueName }) {
  const action = useSelector((rootState) => rootState.keyCondition.action);
  return (
    <>
      {action === KeyConditionAction.ADD ? (
        <Add
          setCurrentStepState={setCurrentStepState}
          setDisplayLabels={setDisplayLabels}
          userGroupUniqueName={userGroupUniqueName}
        />
      ) : (
        <Edit
          setCurrentStepState={setCurrentStepState}
          setDisplayLabels={setDisplayLabels}
          userGroupUniqueName={userGroupUniqueName}
        />
      )}
    </>
  );
}

export default ByIndividualInclusion;
